import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "plumbing-google-maps-colorado",
  metaTitle: "Plumbing Google Maps Optimization in Colorado | GBP for Plumbers",
  metaDescription:
    "Rank in the Google Map Pack for plumbing across Colorado. GBP optimization, review velocity, and service-area tuning. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Plumbing marketing", to: "/plumbing-marketing-colorado" },
  ],
  currentLabel: "Plumbing Google Maps Colorado",
  eyebrow: "Plumbing Google Maps — Colorado",
  h1: (
    <>
      Plumbing <span className="text-brand-gold">Google Maps</span> in Colorado
    </>
  ),
  heroDescription:
    "Own the Map Pack in your Colorado service area — because emergency plumbing calls go to whoever shows up first on the map.",
  intro:
    "The Google Map Pack drives the majority of plumbing calls in Colorado. We optimize your Google Business Profile from the ground up — primary and secondary categories, every relevant service line, weekly Posts, weekly Q&A monitoring, GeoTagged photos from real Colorado jobs, and a review-velocity system that keeps fresh reviews coming in week after week.",
  definition: {
    term: "Plumbing Google Maps optimization in Colorado",
    body: "Google Maps optimization (GBP optimization) is the process of ranking your plumbing company in the local 3-pack on Google Maps for searches like 'plumber near me' or 'water heater repair Colorado Springs.' It involves category selection, service-area tuning, review velocity, and ongoing Posts and photo signals.",
  },
  whatWeDo: [
    { title: "Full GBP audit and rebuild", body: "Categories, services, attributes, hours, service area — every field optimized for Colorado plumbing intent." },
    { title: "Weekly GBP Posts", body: "Service highlights, before/afters, seasonal callouts — published weekly to keep the listing active." },
    { title: "Geo-tagged photos from real jobs", body: "We coach your team on which photos to capture and we upload them with proper geo-tags weekly." },
    { title: "Review velocity tied to dispatch", body: "Automated review requests sent at the right moment after job completion — tied to ServiceTitan, Housecall Pro, or your FSM." },
    { title: "Q&A monitoring", body: "We populate seed questions, monitor for new ones, and respond within hours — not days." },
    { title: "Service-area tuning", body: "Add and prune service areas based on actual ranking data — not guesses." },
  ],
  whyThisMarket: [
    {
      heading: "The Map Pack is the plumbing search results page",
      body: "On mobile, the Map Pack takes up the entire above-the-fold area for plumbing searches in Colorado. If you're not in the top 3, you're invisible to the people calling at midnight.",
    },
    {
      heading: "Reviews and recency dominate the ranking",
      body: "GBP rankings weight review velocity (how recently you got reviews) more heavily than total count. A plumber with 80 reviews from the last 90 days will routinely outrank one with 500 from 3 years ago.",
    },
    {
      heading: "Most plumbers leave 60% of GBP fields blank",
      body: "We routinely find Colorado plumbers with 4-6 services listed when they actually offer 25+. Each missed service is a missed ranking opportunity for that exact term.",
    },
  ],
  relatedLinks: [
    { to: "/plumbing-marketing-colorado", label: "Plumbing marketing in Colorado — full hub" },
    { to: "/colorado-springs-google-maps", label: "Local SEO overview" },
    { to: "/plumbing-seo-colorado", label: "Plumbing SEO in Colorado" },
    { to: "/plumbing-web-design-colorado", label: "Plumbing web design in Colorado" },
    { to: "/colorado-springs-google-maps", label: "Colorado Springs Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/case-studies", label: "Colorado plumbing case studies" },
    { to: "/google-maps", label: "Google Maps optimization (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How long does Google Maps optimization take for plumbers?", a: "Most Colorado plumbers see Map Pack movement in 30–60 days. Plumbing has one of the fastest GBP response times of any vertical because the review signal compounds quickly." },
    { q: "What's the difference between SEO and Google Maps optimization?", a: "SEO ranks your website in the organic results below the map. Google Maps optimization (GBP optimization) ranks your business in the map itself. For plumbing, the Map Pack drives more calls than the organic results — so most plumbers should prioritize Maps first." },
    { q: "Do I need both Google Maps and Local Services Ads?", a: "They feed each other. LSAs give immediate lead flow; GBP optimization compounds long-term. Companies that run both win the most lead share in their Colorado service area." },
    { q: "Do you only serve Colorado Springs plumbers?", a: "No — we serve plumbers across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and beyond. Colorado-only by design." },
  ],
  serviceName: "Plumbing Google Maps Optimization in Colorado",
  serviceDescription:
    "Google Business Profile optimization and Map Pack ranking for plumbing companies across Colorado.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/plumbing-google-maps-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
